import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  UNSUCCESSFUL_REASONS,
  UNSUCCESSFUL_OTHER_REASON,
} from "@/utils/unsuccessful-reasons";
import { useMarkUnsuccessful } from "../hooks/use-mark-unsuccessful";

interface MarkUnsuccessfulDialogProps {
  source_id: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MarkUnsuccessfulDialog({
  source_id,
  open,
  onOpenChange,
}: MarkUnsuccessfulDialogProps) {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [note, setNote] = useState<string | undefined>(undefined);
  const { mutate: markUnsuccessful } = useMarkUnsuccessful();

  const isOtherSelected = selectedReason === UNSUCCESSFUL_OTHER_REASON;

  const reset = () => {
    setSelectedReason("");
    setNote(undefined);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark session as unsuccessful</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <p className="text-sm text-gray-600">
            This session will not count against the user's bundle and will be
            excluded from the audit center's unmapped queue. It stays linked to
            its bundle for the record.
          </p>
          <div className="space-y-2">
            <label className="text-sm font-medium">Select reason</label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {UNSUCCESSFUL_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {reason}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isOtherSelected && (
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Please provide a reason
              </label>
              <Textarea
                placeholder="Type your reason here"
                value={note ?? ""}
                onChange={(e) => setNote(e.target.value || undefined)}
                className="min-h-[100px]"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              reset();
            }}
          >
            Close
          </Button>
          <Button
            className="bg-amber-600 text-white hover:bg-amber-700"
            onClick={() => {
              const finalReason = isOtherSelected ? note : selectedReason;
              if (!finalReason) return;
              markUnsuccessful({ source_id, reason: finalReason });
              reset();
              onOpenChange(false);
            }}
            disabled={!selectedReason || (isOtherSelected && !note)}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
